export const useCurrentUserStore = defineStore('currentUser', () => {
  const { status: authStatus, data: sessionData } = useAuth();
  let currentUserLoadingPromise: Promise<void>;
  const currentUser = ref<UserWithRoles | null>(null);
  const rolesStore = useRolesStore();
  const { roles } = storeToRefs(rolesStore);

  const currentUserGroupNames = computed(() => (currentUser.value?.groups || []).map(group => group.name));
  const currentUserPermissionCodenames = computed(() =>
    (currentUser.value?.roles || [])
      .map(role => role.permissions.map(permission => permission.codename))
      .reduce((acc, permissionList) => [...acc, ...permissionList], []),
  );

  const setCurrentUser = async () => {
    if (authStatus.value === 'unauthenticated' || !sessionData.value) {
      currentUser.value = null;
      return;
    }

    currentUserLoadingPromise = new Promise(resolve => {
      const headers = useRequestHeaders(['cookie']);
      Promise.all([
        rolesStore.fetchRoles(),
        $fetch('/api/users/current', { headers }),
      ]).then(([_, _currentUser]) => {
        if (_currentUser) {
          currentUser.value = {
            ..._currentUser,
            role_ids: _currentUser.roles,
            roles: roles.value.filter(role => _currentUser.roles.includes(role.id)),
          };
        }
        else {
          currentUser.value = _currentUser;
        }
        resolve();
      });
    });
  };

  watch(authStatus, setCurrentUser);
  setCurrentUser();

  const waitForCurrentUser = async () => {
    return await currentUserLoadingPromise;
  };

  const currentUserHas = async (permType: 'group' | 'perm', name: string) => {
    await waitForCurrentUser();
    if (!currentUser.value) {
      return false;
    }

    if (currentUser.value.is_superuser) {
      return true;
    }

    if (permType === 'group') {
      return currentUserGroupNames.value.includes(name);
    }
    else {
      return currentUserPermissionCodenames.value.includes(name);
    }
  };

  const currentUserHasGroup = async (groupName: string) =>
    currentUserHas('group', groupName);

  const currentUserHasSomeGroups = async (groupNames: string[]) =>
    promiseSome(groupNames.map(groupName => currentUserHasGroup(groupName)));

  const currentUserHasAllGroups = async (groupNames: string[]) =>
    promiseEvery(groupNames.map(groupName => currentUserHasGroup(groupName)));

  const currentUserHasPermission = async (permissionName: string) =>
    currentUserHas('perm', permissionName);

  const currentUserHasSomePermissions = async (permissionNames: string[]) =>
    promiseSome(permissionNames.map(permissionName => currentUserHasPermission(permissionName)));

  const currentUserHasAllPermissions = async (permissionNames: string[]) =>
    promiseEvery(permissionNames.map(permissionName => currentUserHasPermission(permissionName)));

  return {
    currentUser,
    currentUserHasGroup,
    currentUserHasSomeGroups,
    currentUserHasAllGroups,
    currentUserHasPermission,
    currentUserHasSomePermissions,
    currentUserHasAllPermissions,
    waitForCurrentUser,
  };
});
